import Typography from "@material-ui/core/Typography";
import React, { FC, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";

import Button from "../../../../../components/elements/Button";
import ConfirmationModal from "../../../../../components/modules/ConfirmationModal";
import Loader from "../../../../../components/elements/Loader";
import api from "../../../../../api";
import classes from "../../AdvertiserDetailsSection.module.scss";
import hubspotLogo from "../../../../../assets/icons/HubspotLogo.svg";
import { AdvertiserType } from "../../../../../types/advertiserTypes";
import { ReduxApplicationState } from "../../../../../types/ReduxTypes/ReduxApplicationState";
import { setAgencyAdvertisers } from "../../../../../redux/reducers/agency";

type HubspotConnectorProps = {
  advertiser: AdvertiserType;
  fetchAndSetAdvertiserDetailData: () => void;
};

const fetchHubspotAuthUrl = async (bgAdvertiserId: string) => {
  try {
    const { payload } = await api.getHubspotAuth(bgAdvertiserId);
    return payload;
  } catch (error) {
    const err = error as any;
    throw new Error(err);
  }
};

export const updateHubspotTokens = async (hubspotCredentialCode: string, bgAdvertiserId: string) => {
  try {
    await api.createHubspotTokens(hubspotCredentialCode, bgAdvertiserId);
  } catch (error) {
    const err = error as any;
    throw new Error(err);
  }
};

const deleteHubspotTokens = async (bgAdvertiserId: string) => {
  try {
    await api.deleteHubspotTokens(bgAdvertiserId);
  } catch (error) {
    const err = error as any;
    throw new Error(err);
  }
};

const HubspotConnector: FC<HubspotConnectorProps> = ({ advertiser, fetchAndSetAdvertiserDetailData }) => {
  const { hubspot_authed, hubspot_portal_id, brandgenAdvertiserId, name: brandgenAdvertiserName } = advertiser;

  const dispatch = useDispatch();
  const agencyId = useSelector((state: ReduxApplicationState) => state.agency.agencyId);
  const [isLoading, setIsLoading] = useState(false);
  const [hubspotAuthUrl, setHubspotAuthUrl] = useState("");
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);

  const location = useLocation();
  const history = useHistory();

  const redirectToHubspot = () => {
    localStorage.setItem("currentBrandgenAdvertiserId", brandgenAdvertiserId);
    window.location.href = hubspotAuthUrl;
  };

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);

    const fetchAndSetHubspotAuthUrl = async () => {
      try {
        const fetchedHubspotAuthUrl = await fetchHubspotAuthUrl(brandgenAdvertiserId);
        setHubspotAuthUrl(fetchedHubspotAuthUrl);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log("error fetchAndSetHubspotAuthUrl: ", error);
      }
    };

    const updateHubspotTokensAndRemoveCodeParam = async () => {
      try {
        const hubspotCredentialCode = window.location.href.split("?").slice(-1)[0].split("=").slice(-1)[0].trim();
        setIsLoading(true);
        await updateHubspotTokens(hubspotCredentialCode, brandgenAdvertiserId);
        await fetchAndSetAdvertiserDetailData();
        dispatch(setAgencyAdvertisers(agencyId));
        queryParams.delete("code");
        history.replace({
          search: queryParams.toString(),
        });
        setIsLoading(false);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log("error updateHubspotTokensAndRefreshPage: ", error);
      }
    };

    if (queryParams.has("code")) {
      updateHubspotTokensAndRemoveCodeParam();
    }

    fetchAndSetHubspotAuthUrl();
  }, [agencyId, brandgenAdvertiserId, dispatch, fetchAndSetAdvertiserDetailData, history, location.search]);

  const disconnectFromHubspot = (bgAdvertiserId: string) => async () => {
    try {
      setIsLoading(true);
      await deleteHubspotTokens(bgAdvertiserId);
      await fetchAndSetAdvertiserDetailData();
      dispatch(setAgencyAdvertisers(agencyId));
      setIsLoading(false);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log("error disconnectFromHubspot: ", error);
    }
  };

  const openConfirmationModal = () => setIsConfirmationModalOpen(true);
  const closeConfirmationModal = () => setIsConfirmationModalOpen(false);

  return (
    <div className={classes.hubspotConnectorContainer}>
      <ConfirmationModal
        isOpen={isConfirmationModalOpen}
        handleClose={closeConfirmationModal}
        handleConfirm={disconnectFromHubspot(brandgenAdvertiserId)}
        confirmButtonLabel="Disconnect"
        confirmQuestion={`Disconnect "${brandgenAdvertiserName}" Advertiser from HubSpot`}
      />

      {isLoading ? (
        <Loader isAbsolutePosition={false} size={36} />
      ) : (
        <div className={classes.connector}>
          <div className={classes.status}>
            <img src={hubspotLogo} alt="hubspot logo" />

            <div className={classes.statusTextContainer}>
              {hubspot_authed ? (
                <Typography className={classes.connected}>Account ID: {hubspot_portal_id}</Typography>
              ) : (
                <Typography className={classes.notConnected}>Not Connected</Typography>
              )}
              <Typography className={classes.title}>HubSpot Account</Typography>
            </div>
          </div>

          <div className={classes.buttons}>
            {hubspot_authed ? (
              <Button onClick={openConfirmationModal} variant="outlined">
                Disconnect
              </Button>
            ) : null}
            <Button onClick={redirectToHubspot} disabled={!hubspotAuthUrl.length} variant="outlined">
              {hubspot_authed ? "Update" : "Connect"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default HubspotConnector;
